import React, { FC, memo } from 'react';
import { useTheme } from '@mui/material';
import moment from 'moment-timezone';

import { Tooltip } from 'src/shared/components';
import { ArrowRightIcon } from 'src/shared/components/icons';

import { MonthlyEventType } from './MonthlyCalendar';
import './MonthlyEvent.styles.scss';

interface Props {
  day: moment.Moment;
  event: MonthlyEventType;
  isLastCellInGrid: boolean;
  showFullInfo?: boolean;
}

export const MonthlyEvent: FC<Props> = memo(({ day, event, isLastCellInGrid, showFullInfo = false }) => {
  const theme = useTheme();

  if (event.unpublished) {
    return null;
  }

  const isFirstDayOfWeek = day.weekday() === 0;
  const isFirst = day.isSame(event.range.startDate, 'day');
  const isLast = day.isSame(event.range.endDate, 'day');
  const eventClass = getEventClass(isFirst || showFullInfo, isLast || showFullInfo);
  const cursorClass = event.onClick ? 'cursor-pointer' : '';
  const containerClass = event.containerClass || '';
  const className = `monthly-event ${event.type} ${eventClass} ${containerClass} ${cursorClass} ${theme.palette.mode}`;

  return (
    <div className={className} onClick={event.onClick}>
      {(isFirst || isFirstDayOfWeek || showFullInfo) && (
        <Tooltip title={event.tooltip} showIcon={false}>
          <div className="d-flex align-items-center justify-content-between w-100" style={{ minWidth: 0 }}>
            <div className="event-title fw-500 me-1">{event.title}</div>
            {event.description && <div className="event-description flex-shrink-0 fw-400">{event.description}</div>}
          </div>
        </Tooltip>
      )}
      {!isLast && isLastCellInGrid && (
        <div className="d-flex justify-content-end flex-grow-1">
          <ArrowRightIcon size={12} />
        </div>
      )}
    </div>
  );
});

const getEventClass = (isFirst: boolean, isLast: boolean) => {
  if (isFirst && isLast) {
    return 'first last';
  }

  if (isFirst) {
    return 'first';
  }

  if (isLast) {
    return 'last';
  }

  return 'overlap';
};
